// 세리사무소 포털 홈 — 「내 일정」 위젯.
//
// 킷 달력(AXErpCalendar)으로 내가 맡은 일(프로젝트 작업·내 업무)·휴가·출장·근태 확인을 월간·주간·일간으로 본다.
// 메뉴나 별도 화면이 아니라 홈 본문의 카드다. 읽기 전용이고, 본인 사번으로만 각 모듈의 조회 API 를 부른다(pages/Schedule/useSchedule.ts).
// 한 모듈이 안 떠 있거나 실패해도 나머지 일정은 그대로 보인다. 일정을 누르면 카드 안에 상세가 열리고, 「원래 화면에서 열기」로 원 모듈에 간다.
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AXErpCalendar, axErpCalendarLocale } from 'yukimushi-component-fe';
import type { AXErpCalendarEvent } from 'yukimushi-component-fe';
import { navigate } from '../../lib/navigation';
import { useModuleRegistry } from '../../lib/moduleRegistryStore';
import { useT } from '../../lib/locale';
import type { MessageKey } from '../../lib/locale.messages';
import { addDays, filterBySource } from '../../lib/schedule';
import type { ScheduleItem, ScheduleSource } from '../../lib/schedule';
import { SOURCES, SOURCE_MODULE, useSchedule } from '../Schedule/useSchedule';
import styles from './Home.zeirishi.schedule.module.css';

const SOURCE_DOT: Record<ScheduleSource, string> = {
  'pjt-task': '#0071e3', 'pjt-todo': '#8e44cf', 'hr-leave': '#248a3d', 'ops-trip': '#0891b2', 'hr-attend': '#c04f00',
};

/** 달력 높이(px) — 사용자가 손잡이를 끌어 조절한다. 범위를 벗어난 값(저장값 손상 포함)은 범위 안으로 맞춘다 */
const H_DEFAULT = 400;
const H_MIN = 260;
const H_MAX = 900;
const H_KEY = 'zeirishiScheduleHeight';

function clampH(v: number): number {
  return Math.min(H_MAX, Math.max(H_MIN, Math.round(v)));
}

function loadHeight(): number {
  try {
    const v = Number(window.localStorage.getItem(H_KEY));
    return Number.isFinite(v) && v > 0 ? clampH(v) : H_DEFAULT;
  } catch {
    // localStorage 접근이 막힌 환경(사파리 프라이빗 등) — 기본 높이로 산다
    return H_DEFAULT;
  }
}

function saveHeight(h: number): void {
  try {
    window.localStorage.setItem(H_KEY, String(h));
  } catch {
    // 저장 실패는 이번 세션만 영향이 있다
  }
}

interface CalEvent extends AXErpCalendarEvent {
  item: ScheduleItem;
}

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 달력이 알려 주기 전의 첫 조회 범위 — 월 보기의 앞뒤 주까지 덮도록 넉넉히 잡는다 */
function initialRange(): { from: string; to: string } {
  const t = todayLocal();
  return { from: addDays(t, -35), to: addDays(t, 42) };
}

function periodText(it: ScheduleItem): string {
  if (!it.allDay) return `${it.start.slice(0, 10)} ${it.start.slice(11, 16)}${it.end ? `–${it.end.slice(11, 16)}` : ''}`;
  return it.end && it.end !== it.start ? `${it.start} ~ ${it.end}` : it.start;
}

export default function ZeirishiScheduleWidget() {
  const { lang, t } = useT();
  const registry = useModuleRegistry();
  const [range, setRange] = useState(initialRange);
  const state = useSchedule(range.from, range.to);

  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);

  /* 높이 조절 — 손잡이를 끌면 그 만큼, 키보드(↑↓)는 40px, 더블클릭은 기본 높이. 놓는 순간 저장한다 */
  const [height, setHeight] = useState(loadHeight);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ y: number; h: number } | null>(null);
  const onGripDown = (e: React.PointerEvent<HTMLDivElement>) => {
    drag.current = { y: e.clientY, h: height };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  };
  const onGripMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current) setHeight(clampH(drag.current.h + (e.clientY - drag.current.y)));
  };
  const onGripUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const h = clampH(drag.current.h + (e.clientY - drag.current.y));
    drag.current = null;
    setDragging(false);
    setHeight(h);
    saveHeight(h);
  };
  const onGripKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let h: number | null = null;
    if (e.key === 'ArrowUp') h = clampH(height - 40);
    else if (e.key === 'ArrowDown') h = clampH(height + 40);
    else if (e.key === 'Home') h = H_MIN;
    else if (e.key === 'End') h = H_MAX;
    if (h === null) return;
    e.preventDefault();
    setHeight(h);
    saveHeight(h);
  };
  const resetHeight = () => { setHeight(H_DEFAULT); saveHeight(H_DEFAULT); };

  const shown = useMemo(() => filterBySource(state.items, enabled), [state.items, enabled]);
  const events: CalEvent[] = useMemo(() => shown.map(i => ({
    id: i.id, title: i.title, start: i.start, end: i.end, allDay: i.allDay, tone: i.tone, location: i.location, locked: true, item: i,
  })), [shown]);
  const calLocale = useMemo(() => axErpCalendarLocale(lang === 'ja' ? 'ja' : 'ko'), [lang]);

  // 필터로 숨겼거나 기간을 옮겨 사라진 일정의 상세는 열어 두지 않는다
  const selected = selectedId ? shown.find(i => i.id === selectedId) ?? null : null;

  const onRangeChange = useCallback((r: { from: string; to: string }) => {
    setRange(prev => (prev.from === r.from && prev.to === r.to ? prev : { from: r.from, to: r.to }));
  }, []);

  const modName = (cd: string) => t(`schedule.mod.${cd}` as MessageKey);
  const srcName = (s: ScheduleSource) => t(`schedule.src.${s}` as MessageKey);
  const uniq = (xs: string[]) => Array.from(new Set(xs)).join(', ');

  const openOriginal = (it: ScheduleItem) => {
    const entry = registry.find(e => e.moduleCd === it.moduleCd);
    navigate(entry ? entry.pathPrefix : `/${it.moduleCd}`);
  };

  return (
    <section className={styles.wrap} aria-label={t('schedule.title')}>
      <div className={styles.card}>
        <div className={styles.head}>
          <div className={styles.ttl}>
            <h2>{t('schedule.title')}</h2>
            <span className={styles.sub}>{t('schedule.widget.sub')}</span>
          </div>
          <div className={styles.chips} role="group" aria-label={t('schedule.filter')}>
            {SOURCES.map(s => {
              const on = enabled[s] !== false;
              const gone = state.failed.indexOf(s) >= 0 || state.missing.indexOf(s) >= 0;
              return (
                <button key={s} type="button" aria-pressed={on} disabled={gone}
                  className={`${styles.chip} ${on ? styles.chipOn : ''}`}
                  onClick={() => setEnabled(prev => ({ ...prev, [s]: prev[s] === false }))}>
                  <i style={{ background: SOURCE_DOT[s] }} aria-hidden="true" />{srcName(s)}
                </button>
              );
            })}
            <button type="button" className={styles.reload} onClick={state.reload} disabled={state.loading}
              title={t('schedule.retry')} aria-label={t('schedule.retry')}>↻</button>
          </div>
        </div>

        {state.noUser && <div className={styles.note} role="alert">{t('schedule.noUser')}</div>}
        {state.failed.length > 0 && (
          <div className={`${styles.note} ${styles.noteErr}`} role="alert">
            {t('schedule.failed', { names: uniq(state.failed.map(srcName)) })}
          </div>
        )}
        {state.missing.length > 0 && (
          <div className={styles.note} role="status">
            {t('schedule.notInstalled', { names: uniq(state.missing.map(s => modName(SOURCE_MODULE[s]))) })}
          </div>
        )}

        {/* 달력은 부모 높이를 채운다(킷 계약) — 높이는 .cal 이 정한다 */}
        <div className={`${styles.cal} ${dragging ? styles.calDrag : ''}`} style={{ height }}>
          <AXErpCalendar<CalEvent>
            events={events}
            views={['month', 'week', 'day']}
            defaultView="week"
            weekStartsOn={1}
            locale={calLocale}
            loading={state.loading}
            onRangeChange={onRangeChange}
            onEventClick={ev => setSelectedId(selectedId === ev.item.id ? null : ev.item.id)}
            aria-label={t('schedule.calAria')}
            style={{ height: '100%' }}
          />
        </div>
        <div className={`${styles.grip} ${dragging ? styles.gripOn : ''}`} role="separator" aria-orientation="horizontal"
          aria-label={t('schedule.widget.resize')} aria-valuemin={H_MIN} aria-valuemax={H_MAX} aria-valuenow={height}
          tabIndex={0} title={t('schedule.widget.resizeHint')}
          onPointerDown={onGripDown} onPointerMove={onGripMove} onPointerUp={onGripUp} onPointerCancel={onGripUp}
          onKeyDown={onGripKey} onDoubleClick={resetHeight}>
          <span aria-hidden="true" />
        </div>

        {selected && (
          <div className={styles.detail}>
            <span className={styles.dsrc}><i style={{ background: SOURCE_DOT[selected.source] }} aria-hidden="true" />{srcName(selected.source)}</span>
            <b className={styles.dtt}>{selected.title}</b>
            <span className={styles.dmeta}>
              {periodText(selected)}
              {selected.statusNm ? ` · ${selected.statusNm}` : ''}
              {selected.location ? ` · ${selected.location}` : ''}
            </span>
            <button type="button" className={styles.dopen} onClick={() => openOriginal(selected)}>{t('schedule.detail.open')}</button>
            <button type="button" className={styles.dx} onClick={() => setSelectedId(null)} aria-label={t('schedule.detail.close')}>✕</button>
          </div>
        )}
      </div>
    </section>
  );
}
